import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { pitchKindsOfGoal } from 'fym-shared/src/music24/outreach/dailyPitches';
import { TypedMemo } from '../../../common/types';
import type { CampaignDetail } from '../outreachTypes';
import type { ReleaseOption } from '../hooks/useReleaseOptions';
import { CampaignGoal, useCampaignActions } from '../hooks/useCampaignActions';
import { useMailboxes } from '../hooks/useMailboxes';
import { BuilderNav } from './BuilderNav';
import { BuilderSummary } from './BuilderSummary';
import { goalTitleOf } from './GoalCards';
import { StepMessage } from './StepMessage';
import { StepSendFrom } from './StepSendFrom';
import { StepSongGoal } from './StepSongGoal';
import { StepStart } from './StepStart';
import { StepWho } from './StepWho';
import { BUILDER_STEPS, BuilderStep, nextStep, previousStep } from './builderSteps';
import { campaignMessages as messages } from './campaignMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the setup; each part uses its own slice of it */
import styles from './Builder.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface BuilderBodyProps {
  accountId: number;
  campaign: CampaignDetail;
  songOptions: ReleaseOption[];
  readOnly: boolean;
  /** A campaign past the draft ("Edit setup"): the song is locked, Start saves the changes. */
  isEditing: boolean;
  step: BuilderStep;
  onStep: (step: BuilderStep) => void;
  onCancel: () => void;
}

/** The steps of a draft or of a running campaign (design outreach-v2 b1 layout): the nav, the step, the summary. */
const BuilderBodyComponent: React.FC<BuilderBodyProps> = ({
  accountId,
  campaign,
  songOptions,
  readOnly,
  isEditing,
  step,
  onStep,
  onCancel,
}) => {
  const intl = useIntl();
  const actions = useCampaignActions(accountId);
  const { mailboxes } = useMailboxes(accountId);
  const [songFailed, setSongFailed] = useState(false);
  const next = useCallback(() => onStep(nextStep(step)), [onStep, step]);
  const back = useCallback(() => onStep(previousStep(step)), [onStep, step]);

  // The Song step of a draft: another song or goal. The goal sets the kinds again; a generated
  // name follows the song, a name that the artist set stays.
  const changeSong = useCallback(
    (catalogItemUuid: string, goal: CampaignGoal) => {
      if (catalogItemUuid === campaign.catalogItemUuid && goal === campaign.goal) {
        next();
        return;
      }
      const song = songOptions.find((option) => option.catalogItemUuid === catalogItemUuid);
      const keepsName = campaign.name === intl.formatMessage(messages.campaignName, { song: campaign.songTitle });
      setSongFailed(false);
      actions
        .updateCampaign({
          campaignUuid: campaign.campaignUuid,
          goal,
          ...(goal === campaign.goal ? {} : { kinds: pitchKindsOfGoal(goal) }),
          ...(catalogItemUuid === campaign.catalogItemUuid ? {} : { catalogItemUuid }),
          ...(keepsName && song !== undefined
            ? { name: intl.formatMessage(messages.campaignName, { song: song.title }) }
            : {}),
        })
        .then(next)
        .catch(() => setSongFailed(true));
    },
    [actions, campaign, intl, next, songOptions]
  );

  const subs = useMemo(() => {
    const from = mailboxes.find((mailbox) => campaign.mailboxUuids.includes(mailbox.mailboxUuid));
    const done = (item: BuilderStep) => BUILDER_STEPS.indexOf(item) < BUILDER_STEPS.indexOf(step);
    return {
      song: intl.formatMessage(messages.navSong, {
        song: campaign.songTitle,
        goal: intl.formatMessage(goalTitleOf(campaign.goal)).toLowerCase(),
      }),
      ...(done('who')
        ? { who: intl.formatMessage(messages.navWho, { kinds: campaign.kinds.length, rate: campaign.dailyRate }) }
        : {}),
      ...(done('message') && campaign.angle === null ? { message: intl.formatMessage(messages.navMessageAuto) } : {}),
      ...(done('sendFrom') && from !== undefined ? { sendFrom: from.address } : {}),
    };
  }, [campaign, intl, mailboxes, step]);

  const pane = () => {
    switch (step) {
      case 'song':
        return (
          <StepSongGoal
            accountId={accountId}
            options={songOptions}
            initialSong={campaign.catalogItemUuid}
            initialGoal={campaign.goal}
            songLocked={isEditing || campaign.startedAt !== null}
            onNext={changeSong}
            onCancel={onCancel}
            isSaving={actions.isSaving}
            failed={songFailed ? 'change' : null}
            disabled={readOnly}
          />
        );
      case 'who':
        return <StepWho accountId={accountId} campaign={campaign} readOnly={readOnly} onBack={back} onNext={next} />;
      case 'message':
        return <StepMessage accountId={accountId} campaign={campaign} onBack={back} onNext={next} />;
      case 'sendFrom':
        return (
          <StepSendFrom accountId={accountId} campaign={campaign} readOnly={readOnly} onBack={back} onNext={next} />
        );
      case 'start':
      default:
        return (
          <StepStart
            accountId={accountId}
            campaign={campaign}
            readOnly={readOnly}
            isEditing={isEditing}
            onStep={onStep}
          />
        );
    }
  };

  return (
    <div className={clsx(styles.layout, step === 'start' && styles.layoutNoSum)}>
      <BuilderNav step={step} onStep={onStep} subs={subs} />
      <div className={clsx(styles.stepCol, step === 'start' && styles.stepColNarrow)}>{pane()}</div>
      {step !== 'start' && <BuilderSummary accountId={accountId} campaign={campaign} step={step} />}
    </div>
  );
};

export const BuilderBody = TypedMemo(BuilderBodyComponent);
